import { useCallback, useMemo } from "react";

import { sortAlphabetically } from "Libs/utils";

import getMetricsV2 from "../api/getMetrics/getMetricsV2";
import { DG2_QUERY, DG3_QUERY, GRID_QUERY } from "../resources/settings";
import { TimeFrameType } from "../resources/types";
import usePoller from "../utils/polling/usePoller";
import {
  explodeMetrics,
  groupByTimestamp,
  mergeSameTimestampData
} from "../utils/transformMetrics";

import useMetricsLinkData from "./useMetricsLinkData";

const formatData = data => {
  if (!data?.length) {
    return undefined;
  }
  const metrics = explodeMetrics(data);
  const mountpoints = sortAlphabetically(
    Array.from(
      new Set(
        metrics
          .map(({ mountpoint }) => mountpoint)
          .filter(mountpoint => mountpoint != null && mountpoint !== "/tmp")
      )
    )
  ).reduce((mountpoints, mountpoint) => {
    const mountpointMetrics = groupByTimestamp(
      metrics.filter(point => point.mountpoint === mountpoint)
    ).map(mergeSameTimestampData);

    if (mountpointMetrics.length) {
      return {
        ...(mountpoints as Record<string, unknown>),
        [mountpoint as string]: mountpointMetrics
      };
    }

    return mountpoints;
  }, {});
  const cpu = groupByTimestamp(
    metrics.filter(point => point.type === "cpu")
  ).map(mergeSameTimestampData);
  const memory = groupByTimestamp(
    metrics.filter(point => point.type === "memory")
  ).map(mergeSameTimestampData);

  return {
    cpu,
    memory,
    ...(mountpoints as Record<string, Record<string, unknown>>)
  };
};

const getQuery = (name: string, type: string, collection: string) => ({
  fields: { dedicated: DG2_QUERY, grid: GRID_QUERY }[type] || DG3_QUERY,
  stream: { collection, stream: "metrics" },
  filters: [{ key: "service", value: name }]
});

const useServiceMetrics = (name: string, timeFrame: TimeFrameType) => {
  const { collection, type, deploymentType, href } = useMetricsLinkData();

  // deployment type exposed on Git15; fallback to #metrics link type for previous versions of Git
  const target = deploymentType ?? type;

  const query = useMemo(
    () => getQuery(name, target, collection),
    [name, target, collection]
  );

  const requestFn = useCallback(
    () =>
      getMetricsV2({
        url: href,
        query,
        timeFrame
      }),
    [href, query, timeFrame]
  );

  const { data, status, error } = usePoller({
    requestFn,
    timeFrame,
    formatData
  });

  return {
    data,
    status,
    error
  };
};

export default useServiceMetrics;
